import { useState } from 'react';
import { Transaction, AiDecision, DecisionLabel } from '../types/index.js';

interface Props {
  transaction: Transaction;
  index: number;
  total: number;
  aiDecision?: AiDecision;
  aiVsHuman: boolean;
  onDecide: (decision: DecisionLabel) => void;
}

const DECISIONS: { label: DecisionLabel; icon: string; className: string }[] = [
  { label: 'Legitimate', icon: '✓', className: 'btn-legitimate' },
  { label: 'Suspicious', icon: '?', className: 'btn-suspicious' },
  { label: 'Fraudulent', icon: '✗', className: 'btn-fraudulent' },
];

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(amount);
}

function formatTime(iso: string): string {
  return new Intl.DateTimeFormat('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

export function TransactionCard({ transaction, index, total, aiDecision, aiVsHuman, onDecide }: Props) {
  const [userChoice, setUserChoice] = useState<DecisionLabel | null>(null);
  const [showAi, setShowAi] = useState(false);

  function handleDecide(decision: DecisionLabel) {
    if (userChoice) return;
    setUserChoice(decision);
    if (aiVsHuman && aiDecision) {
      setShowAi(true);
      // Auto-advance after 2.5s
      setTimeout(() => {
        setUserChoice(null);
        setShowAi(false);
        onDecide(decision);
      }, 2500);
    } else {
      onDecide(decision);
    }
  }

  const riskColor =
    aiDecision
      ? aiDecision.riskScore >= 66
        ? '#ef4444'
        : aiDecision.riskScore >= 26
        ? '#f59e0b'
        : '#22c55e'
      : undefined;

  return (
    <div className="transaction-card">
      <div className="card-header">
        <span className="card-counter">{index + 1} / {total}</span>
        <span className="card-merchant">{transaction.merchant}</span>
        <span className="card-amount">{formatCurrency(transaction.amount, transaction.currency)}</span>
      </div>

      <div className="card-meta">
        <div className="meta-row">
          <span className="meta-icon">📍</span>
          <span>{transaction.location}</span>
        </div>
        <div className="meta-row">
          <span className="meta-icon">🕐</span>
          <span>{formatTime(transaction.time)}</span>
        </div>
        <div className="meta-row">
          <span className="meta-icon">📱</span>
          <span>{transaction.device}</span>
        </div>
      </div>

      <div className="card-note">
        <span className="note-label">Context</span>
        <p>{transaction.behaviourNote}</p>
      </div>

      {showAi && aiDecision ? (
        <div className="ai-reveal">
          <div className="ai-reveal-header">
            <span className="ai-icon">AI</span>
            <span>AI Verdict</span>
            <span
              className="ai-risk-score"
              style={{ color: riskColor }}
            >
              Risk: {aiDecision.riskScore}%
            </span>
          </div>
          <div
            className={`ai-decision-badge decision-${aiDecision.decision.toLowerCase()}`}
          >
            {aiDecision.decision}
          </div>
          <ul className="ai-reasons">
            {aiDecision.reasons.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
          {userChoice !== aiDecision.decision && (
            <p className="ai-disagree">You and AI disagreed on this one!</p>
          )}
        </div>
      ) : (
        <div className="decision-buttons">
          {DECISIONS.map(({ label, icon, className }) => (
            <button
              key={label}
              className={`decision-btn ${className} ${userChoice === label ? 'selected' : ''}`}
              onClick={() => handleDecide(label)}
              disabled={!!userChoice}
            >
              <span className="btn-icon">{icon}</span>
              <span className="btn-label">{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
